'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';

import Link from '~/(tenant)/_components/tenant-link';

import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';

import { DateStrip } from '~/(tenant)/book/_components/date-strip';
import { getAllBookingSlotsBatch } from '~/(tenant)/book/_lib/server/server-actions';

import type {
  CoachInfo,
  TrainerTemplate,
} from '../_lib/server/coaches-page.loader';
import { CoachCard } from './coach-card';

interface CoachesListProps {
  coaches: CoachInfo[];
  trainerTemplates: TrainerTemplate[];
  /** Tenant's advance-booking horizon in days; NULL when unconfigured (falls back to 30, matching /book). */
  maxAdvanceBookingDays: number | null;
}

export interface CoachSlot {
  startTime: string;
  endTime: string;
  isPeak: boolean;
  templateId: string;
  duration: number;
  price: number;
  sportSlug: string;
}

interface CoachWithSlots {
  coach: CoachInfo;
  slots: CoachSlot[];
}

function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function CoachesList({
  coaches,
  trainerTemplates,
  maxAdvanceBookingDays,
}: CoachesListProps) {
  const { t, i18n } = useTranslation('coaches');
  const hasSchedule = trainerTemplates.length > 0;
  // Cap the date strip to the tenant's advance-booking horizon, mirroring /book
  // (booking-form.tsx defaults to 30 days when the tenant hasn't set a limit).
  const daysToShow = maxAdvanceBookingDays ?? 30;
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [coachSlots, setCoachSlots] = useState<Map<string, CoachSlot[]>>(
    new Map(),
  );
  const [isPending, startTransition] = useTransition();
  // Tracks whether we have completed at least one fetch, so we can distinguish
  // "still loading" from "fetched and no results found"
  const [hasFetched, setHasFetched] = useState(false);

  const fetchSlotsForDate = useCallback(
    async (date: Date) => {
      if (trainerTemplates.length === 0) return;

      const dateStr = formatDateKey(date);

      // ONE batched server-action call covering every trainer template. Each
      // template may expose different coaches/durations, so all of them are
      // queried — but the fan-out now happens server-side in parallel
      // (allSettled inside the action isolates individual failures), instead
      // of one action POST per template per date tap.
      const templatesById = new Map(trainerTemplates.map((t) => [t.id, t]));
      // try/catch: a transport-level rejection of the single POST (offline
      // device, action route error) must degrade to the empty-slots state,
      // not strand the page in "still loading" — the old per-template calls
      // each carried their own catch, and this one POST now carries them all.
      const batch = await getAllBookingSlotsBatch(
        trainerTemplates.map((t) => t.id),
        dateStr,
        'trainer',
      ).catch(() => ({
        success: false as const,
        error: 'Failed to fetch slots',
      }));
      const results = batch.success
        ? batch.results.flatMap(({ templateId, result }) => {
            const template = templatesById.get(templateId);
            if (!template) return [];
            return [{ template, slots: result.data ?? [] }];
          })
        : [];

      // Fan-in: collect every available slot keyed by coach (resource) ID.
      // We deliberately retain all slots at this stage before deduplication so
      // that the templateId associated with each unique startTime is the one
      // from the template that first offered that slot (shortest duration wins
      // because trainerTemplates is sorted by duration ascending in the loader).
      const slotsByCoach = new Map<string, CoachSlot[]>();

      for (const { template, slots } of results) {
        for (const slot of slots) {
          if (!slot.is_available || !slot.available_resources) continue;

          for (const resource of slot.available_resources) {
            const existing = slotsByCoach.get(resource.id) ?? [];
            existing.push({
              startTime: slot.start_time,
              endTime: slot.end_time,
              isPeak: slot.is_peak ?? false,
              templateId: template.id,
              duration: template.duration,
              price: resource.normal_price,
              sportSlug: template.sportSlug,
            });
            slotsByCoach.set(resource.id, existing);
          }
        }
      }

      // Deduplicate: when multiple templates produce a slot at the same
      // startTime for the same coach, keep the first occurrence (shortest
      // duration, since the loader sorts templates ascending by duration).
      // This ensures the templateId in the booking URL always matches the
      // slot that is displayed to the user.
      for (const [coachId, slots] of slotsByCoach) {
        const seen = new Set<string>();
        const unique = slots.filter((s) => {
          if (seen.has(s.startTime)) return false;
          seen.add(s.startTime);
          return true;
        });
        unique.sort((a, b) => a.startTime.localeCompare(b.startTime));
        slotsByCoach.set(coachId, unique);
      }

      setCoachSlots(slotsByCoach);
      setHasFetched(true);
    },
    [trainerTemplates],
  );

  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      startTransition(() => {
        fetchSlotsForDate(date);
      });
    },
    [fetchSlotsForDate],
  );

  // Trigger the initial slot fetch on mount. useEffect is the correct tool
  // here — useMemo must never be used to trigger side effects because React
  // may call it multiple times (Strict Mode, concurrent rendering).
  useEffect(() => {
    if (trainerTemplates.length === 0) return;
    startTransition(() => {
      fetchSlotsForDate(selectedDate);
    });
    // Run only on mount; selectedDate is stable at this point (initial state)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Split coaches into available (with slots) and unavailable
  const { available, unavailable } = useMemo(() => {
    const available: CoachWithSlots[] = [];
    const unavailable: CoachInfo[] = [];

    for (const coach of coaches) {
      const slots = coachSlots.get(coach.id);
      if (slots && slots.length > 0) {
        available.push({ coach, slots });
      } else {
        unavailable.push(coach);
      }
    }

    return { available, unavailable };
  }, [coaches, coachSlots]);

  // Deep-link to a specific coach via the URL hash (#coach-<id>), used by
  // the native home coaches strip. The card may not be in the DOM yet on
  // mount (slot fetch is still in flight, transition is pending). Poll
  // briefly via rAF so the scroll lands as soon as the card renders, then
  // stop. The ref guards against re-scrolling after the user changes dates.
  const hashScrolledRef = useRef(false);
  useEffect(() => {
    if (hashScrolledRef.current) return;
    if (typeof window === 'undefined') return;
    const hash = window.location.hash;
    if (!hash.startsWith('#coach-')) return;
    const id = hash.slice(1);

    let attempts = 0;
    let frame = 0;

    const tryScroll = () => {
      if (hashScrolledRef.current) return;
      const el = document.getElementById(id);
      if (el) {
        hashScrolledRef.current = true;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      // ~2s budget (60 frames * ~33ms with rAF backpressure) — plenty for
      // the slot fetch to resolve without polling forever.
      if (attempts++ < 60) {
        frame = requestAnimationFrame(tryScroll);
      }
    };

    frame = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(frame);
  }, [available, unavailable, hasFetched]);

  if (coaches.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-4 py-12 text-center"
        data-test="coaches-empty-tenant"
      >
        <p className="text-muted-foreground">
          {t('noCoaches', 'No coaches are available at this time.')}
        </p>
        <Button asChild>
          <Link href="/book">{t('bookCourtCta', 'Book a Court')}</Link>
        </Button>
      </div>
    );
  }

  // No tenant-wide coach schedule — show bios only, no calendar/slot UI
  if (!hasSchedule) {
    return (
      <div className="space-y-4" data-test="coaches-bios-only">
        {coaches.map((coach) => (
          <CoachCard key={coach.id} coach={coach} slots={[]} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date selector */}
      <div>
        <p className="text-muted-foreground mb-2 text-sm font-medium">
          {t('selectDate', 'Select date:')}
        </p>
        <DateStrip
          selected={selectedDate}
          onSelect={handleDateSelect}
          daysToShow={daysToShow}
          language={i18n.language}
        />
      </div>

      {/* Loading state */}
      {isPending && (
        <div
          className="flex items-center justify-center py-8"
          role="status"
          aria-label={t('loading', 'Loading available slots...')}
          data-test="coaches-loading"
        >
          <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
        </div>
      )}

      {/* aria-live region: announces result updates to screen readers after
          each slot fetch without moving focus */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {!isPending &&
          hasFetched &&
          (available.length > 0
            ? t('slotsLoaded', '{{count}} coaches available', {
                count: available.length,
              })
            : t('noCoachesForDate', 'No coaches are available on this date.'))}
      </div>

      {/* Available coaches */}
      {!isPending && (
        <>
          {available.length > 0 && (
            <div data-test="coaches-available">
              <h2 className="mb-4 text-lg font-semibold">
                {t('selectInstructor', 'Select instructor:')}
              </h2>
              <div className="space-y-4">
                {available.map(({ coach, slots }) => (
                  <CoachCard
                    key={coach.id}
                    coach={coach}
                    slots={slots}
                    selectedDate={selectedDate}
                    locationSlug={coach.locationSlug}
                  />
                ))}
              </div>
            </div>
          )}

          {/* No coaches available for the selected date */}
          {hasFetched && available.length === 0 && (
            <div className="py-8 text-center" data-test="coaches-empty">
              <p className="text-muted-foreground">
                {t(
                  'noCoachesForDate',
                  'No coaches are available on this date.',
                )}
              </p>
            </div>
          )}

          {/* Coaches with no availability on this date, shown dimmed so the
              user knows they exist — and so deep links from the native home
              strip (#coach-<id>) still resolve to a real DOM element when
              the target coach has no slots today. */}
          {unavailable.length > 0 && (
            <div data-test="coaches-unavailable">
              <p className="text-muted-foreground mb-3 text-sm">
                {t('unavailableOnDate', 'Not available on this date:')}
              </p>
              <div className="space-y-4">
                {unavailable.map((coach) => (
                  <CoachCard key={coach.id} coach={coach} slots={[]} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
